/**
 * Feld- und spezifikationsgenauer Vergleich zwischen dem aktuellen IDS-myCheck im Editor und einem
 * von chatBIC erzeugten IDS-Vorschlag (`MyCheckProposal.document`, `kind: 'IDS'`).
 * Reine Diff-Logik, kein Backend- oder Bot-Zugriff.
 *
 * Eigene Strategie statt `computeMyCheckDiff` (KI):
 * - IDS-Spezifikationen haben keine persistente ID – `IdsAdapterService.fromXml` vergibt bei jedem
 *   Parsen neue UUIDs. Zugeordnet wird deshalb über `specification.name`; eine umbenannte
 *   Spezifikation erscheint als entfernt + neu.
 * - Facetten werden über ihre Identität zugeordnet, nicht über die Position (BIC-Backend#234): Typ plus
 *   Kennung ({@link facetIdentity}, z. B. Entitätsname, Property-Set + Eigenschaftsname). Gleiche
 *   Identitäten werden der Reihe nach gepaart (n-te zu n-ter). Was danach übrig bleibt, wird unter den
 *   Übriggebliebenen über die Position gepaart, aber nur bei gleichem Typ (z. B. eine umbenannte
 *   Eigenschaft) – dann wird feldgenau verglichen. Eine übrige Facette des Vorschlags gilt als Ganzes als
 *   neu (eigene Karte), eine übrige des Ist-Stands als entfernt ({@link IdsSpecChanges.removedFacets}).
 *   Eine vorn eingefügte Facette verschiebt so die übrigen nicht mehr in „geändert".
 * - IFC-Namen (Entität, vordefinierter Typ, Entitätengruppe, „Teil von") werden ohne Groß-/Kleinschreibung
 *   verglichen: das Backend schrieb sie früher in Großbuchstaben (`IFCSLAB`), gespeicherte Regeln
 *   enthalten beide Schreibweisen. Werte von Eigenschaften, Attributen und Klassifikationen bleiben
 *   schreibungsgenau.
 *
 * Pfade von Facetten zählen in der Liste des VORSCHLAGS – der Editor zeigt nach dem Anwenden dessen
 * Facetten. Entfernte Facetten tragen dagegen ihre Position im Ist-Stand.
 *
 * Beide Seiten müssen vorher gleich normalisiert sein (XML-Rundlauf über den Adapter), sonst melden
 * Formunterschiede wie `''` vs. `undefined` unveränderte Felder als geändert.
 */
import {
  FacetType,
  IDSFacet,
  IDSInfo,
  IDSRule,
  IDSSpecification,
  IDSValueField
} from '../../checks/bimCheck/components/ids/models/ids.model';
import {MyCheckFieldChange} from './mycheck-diff';

export interface IdsProposalDocument {
  name?: string;
  description?: string;
  idsXml: string;
}

export type IdsFacetBlock = 'applicability' | 'requirements';

export interface IdsSpecChanges {
  /** Spezifikationen, die nur im Vorschlag vorkommen (Referenzen auf die Objekte aus `proposed`). */
  added: IDSSpecification[];
  /** Spezifikationen, die der Vorschlag entfernt (Referenzen auf die Objekte aus `current`). */
  removed: IDSSpecification[];
  /**
   * Spezifikationsname → Pfad → Vorher/Nachher. Pfade: `description`, `necessity`, `instruction`,
   * `ifcVersions`, `identifier`, je Facette `applicability.<i>.<feld>` / `requirements.<i>.<feld>`,
   * `applicability.<i>` / `requirements.<i>` für eine neue Facette. `<i>` ist die Position im Vorschlag.
   */
  changed: Map<string, Map<string, MyCheckFieldChange>>;
  /**
   * Spezifikationsname (wie in {@link changed}) → Facetten, die der Vorschlag aus dieser Spezifikation
   * entfernt. Sie stehen nach dem Anwenden nicht mehr im Formular; nur Einträge mit mindestens einer
   * Facette.
   */
  removedFacets: Map<string, RemovedIdsFacet[]>;
  /** Neuer Name → alter Name, für umbenannte Spezifikationen (Pfad `name` in {@link changed}). */
  renamedFrom: Map<string, string>;
}

/** Eine Facette des Ist-Stands, die der Vorschlag entfernt. */
export interface RemovedIdsFacet {
  block: IdsFacetBlock;
  /** Die Facette aus dem (normalisierten) Ist-Stand. */
  facet: IDSFacet;
  /** Position im Ist-Stand, zum Wiederherstellen an der alten Stelle. */
  index: number;
}

export interface IdsDiff {
  /** Pfad (`info.title`, `info.author`, …) → Vorher/Nachher der IDS-Metadaten. */
  fieldChanges: Map<string, MyCheckFieldChange>;
  specChanges: IdsSpecChanges;
}

export const IDS_INFO_FIELDS: (keyof IDSInfo)[] = [
  'title', 'copyright', 'version', 'description', 'author', 'date', 'purpose', 'milestone'
];

const SPEC_FIELDS: (keyof IDSSpecification)[] = ['description', 'necessity', 'instruction', 'ifcVersions', 'identifier'];

/** Die Felder, die der Facetten-Editor je Typ tatsächlich anzeigt (siehe `ids-facet.component.html`). */
const FACET_FIELDS: Record<FacetType, (keyof IDSFacet)[]> = {
  entity: ['entityName', 'predefinedType', 'includeSubtypes'],
  entityGroup: ['entityName'],
  attribute: ['attributeName', 'attributeValue'],
  property: ['propertySet', 'propertyName', 'propertyDataType', 'propertyValue'],
  classification: ['classificationSystem', 'classificationValue'],
  material: ['materialName'],
  partOf: ['relation', 'partOfEntityName', 'partOfPredefinedType'],
};

/**
 * IFC-Namen, die ohne Groß-/Kleinschreibung verglichen werden (`IfcSlab` = `IFCSLAB`). Nur diese –
 * Eigenschafts-, Attribut- und Klassifikationswerte bleiben schreibungsgenau.
 */
const CASE_INSENSITIVE_FIELDS: Partial<Record<FacetType, (keyof IDSFacet)[]>> = {
  entity: ['entityName', 'predefinedType'],
  entityGroup: ['entityName'],
  partOf: ['partOfEntityName', 'partOfPredefinedType'],
};

/** Nur bei Anforderungen sichtbar (Abschnitt „Metainformationen"). */
const REQUIREMENT_META_FIELDS: (keyof IDSFacet)[] = ['metaDescription', 'metaNecessity', 'metaUri', 'metaInstruction'];

/**
 * Vergleicht den aktuellen Editor-Zustand (`current`) mit dem Vorschlag (`proposed`).
 * Identische Regelwerke ergeben einen leeren Diff.
 */
export function computeIdsDiff(current: IDSRule, proposed: IDSRule): IdsDiff {
  const fieldChanges = new Map<string, MyCheckFieldChange>();
  for (const field of IDS_INFO_FIELDS) {
    diffField(`info.${field}`, current.info?.[field], proposed.info?.[field], fieldChanges);
  }
  return {
    fieldChanges,
    specChanges: diffSpecifications(current.specifications ?? [], proposed.specifications ?? []),
  };
}

/** Pfade eines Facettenfelds, wie sie in {@link IdsSpecChanges.changed} stehen. */
export function facetFieldPath(block: IdsFacetBlock, index: number, field: string): string {
  return `${block}.${index}.${field}`;
}

/** Pfad einer ganzen Facette (neu oder im Typ geändert), im Gegensatz zu {@link facetFieldPath}. */
export function facetPath(block: IdsFacetBlock, index: number): string {
  return `${block}.${index}`;
}

/** Schlüssel in {@link facetChangesOf}, der die ganze Facette meint (neu bzw. Typ geändert). */
export const WHOLE_FACET = '*';

/**
 * Die Änderungen genau einer Facette, mit dem Feldnamen als Schlüssel – so, wie
 * `IdsFacetComponent` sie braucht. `undefined`, wenn die Facette unverändert ist.
 */
export function facetChangesOf(
  specChanges: Map<string, MyCheckFieldChange> | undefined,
  block: IdsFacetBlock,
  index: number,
): Map<string, MyCheckFieldChange> | undefined {
  if (!specChanges) return undefined;
  const prefix = `${block}.${index}.`;
  let result: Map<string, MyCheckFieldChange> | undefined;
  for (const [path, change] of specChanges) {
    if (path === facetPath(block, index)) {
      result ??= new Map();
      result.set(WHOLE_FACET, change);
      continue;
    }
    if (!path.startsWith(prefix)) continue;
    result ??= new Map();
    result.set(path.slice(prefix.length), change);
  }
  return result;
}

function specKey(spec: IDSSpecification): string {
  return (spec.name ?? '').trim();
}

/**
 * Zuordnung über den Namen. Doppelte Namen (vom Schema erlaubt) werden der Reihe nach zugeordnet:
 * die n-te Spezifikation eines Namens im Vorschlag zur n-ten gleichnamigen im Ist-Stand.
 */
function diffSpecifications(current: IDSSpecification[], proposed: IDSSpecification[]): IdsSpecChanges {
  const unmatched = new Map<string, IDSSpecification[]>();
  for (const spec of current) {
    const key = specKey(spec);
    unmatched.set(key, [...(unmatched.get(key) ?? []), spec]);
  }

  const added: IDSSpecification[] = [];
  const changed = new Map<string, Map<string, MyCheckFieldChange>>();
  const removedFacets = new Map<string, RemovedIdsFacet[]>();
  for (const proposedSpec of proposed) {
    const key = specKey(proposedSpec);
    const candidates = unmatched.get(key);
    const currentSpec = candidates?.shift();
    if (!currentSpec) {
      added.push(proposedSpec);
      continue;
    }
    const pairDiff = diffSpecPair(currentSpec, proposedSpec);
    if (pairDiff.changes.size > 0) {
      changed.set(key, pairDiff.changes);
    }
    if (pairDiff.removedFacets.length > 0) {
      removedFacets.set(key, pairDiff.removedFacets);
    }
  }

  const unpaired = [...unmatched.values()].flat();
  const renamedFrom = new Map<string, string>();
  const renamed = pairRenamedSpecifications(current, proposed, added, unpaired, changed, removedFacets, renamedFrom);
  const removed = unpaired.filter(spec => !renamed.has(spec));
  return {added, removed, changed, removedFacets, renamedFrom};
}

/** So viele weitere Abweichungen darf eine Spezifikation neben dem Namen haben, um noch als dieselbe zu gelten. */
const MAX_CHANGES_FOR_RENAME = 3;

/**
 * Eine umbenannte Spezifikation fände über den Namen kein Gegenstück und erschiene als entfernt + neu –
 * dann gäbe es keine Feldmarkierung am Titel. Deshalb: eine neue und eine entfernte Spezifikation an
 * derselben Listenposition gelten als dieselbe, wenn sie sich außer dem Namen nur wenig unterscheiden.
 * Liefert die dabei zugeordneten alten Spezifikationen.
 */
function pairRenamedSpecifications(
  current: IDSSpecification[],
  proposed: IDSSpecification[],
  added: IDSSpecification[],
  removed: IDSSpecification[],
  changed: Map<string, Map<string, MyCheckFieldChange>>,
  removedFacets: Map<string, RemovedIdsFacet[]>,
  renamedFrom: Map<string, string>,
): Set<IDSSpecification> {
  const paired = new Set<IDSSpecification>();
  for (const addedSpec of [...added]) {
    const oldSpec = current[proposed.indexOf(addedSpec)];
    const newKey = specKey(addedSpec);
    if (!oldSpec || !removed.includes(oldSpec) || paired.has(oldSpec) || changed.has(newKey) || removedFacets.has(newKey)) continue;
    const pairDiff = diffSpecPair(oldSpec, addedSpec);
    if (pairDiff.changes.size + pairDiff.removedFacets.length > MAX_CHANGES_FOR_RENAME) continue;
    const renamed = new Map<string, MyCheckFieldChange>([['name', {before: oldSpec.name, after: addedSpec.name}]]);
    pairDiff.changes.forEach((change, path) => renamed.set(path, change));
    changed.set(newKey, renamed);
    if (pairDiff.removedFacets.length > 0) removedFacets.set(newKey, pairDiff.removedFacets);
    renamedFrom.set(newKey, specKey(oldSpec));
    paired.add(oldSpec);
    added.splice(added.indexOf(addedSpec), 1);
  }
  return paired;
}

interface SpecPairDiff {
  changes: Map<string, MyCheckFieldChange>;
  removedFacets: RemovedIdsFacet[];
}

function diffSpecPair(current: IDSSpecification, proposed: IDSSpecification): SpecPairDiff {
  const changes = new Map<string, MyCheckFieldChange>();
  const removedFacets: RemovedIdsFacet[] = [];
  for (const field of SPEC_FIELDS) {
    diffField(field, current[field], proposed[field], changes);
  }
  diffFacetBlock('applicability', current.applicability ?? [], proposed.applicability ?? [], changes, removedFacets);
  diffFacetBlock('requirements', current.requirements ?? [], proposed.requirements ?? [], changes, removedFacets);
  return {changes, removedFacets};
}

/**
 * Kennung einer Facette für die Zuordnung, ohne Groß-/Kleinschreibung und Leerraum am Rand. Facetten
 * mit gleicher Kennung gelten als dieselbe Facette, auch wenn sie im Vorschlag woanders stehen.
 */
export function facetIdentity(facet: IDSFacet): string {
  const parts = ((): string[] => {
    switch (facet.type) {
      case 'entity': return [valueIdentity(facet.entityName)];
      case 'attribute': return [valueIdentity(facet.attributeName)];
      case 'property': return [valueIdentity(facet.propertySet), valueIdentity(facet.propertyName)];
      case 'classification': return [valueIdentity(facet.classificationSystem)];
      // Ohne Materialnamen bleibt nur der Typ.
      case 'material': return [valueIdentity(facet.materialName)];
      case 'partOf': return [(facet.relation ?? '').trim().toLowerCase(), valueIdentity(facet.partOfEntityName)];
      // Eine Entitätengruppe hat keinen eigenen Namen – der Typ allein ist die Kennung.
      case 'entityGroup': return [];
    }
  })();
  return [facet.type, ...parts].join('|');
}

/**
 * Vergleichsform eines Wertfelds für {@link facetIdentity}: ein einfacher Wert und eine Aufzählung mit
 * genau diesem einen Wert sind gleich; die Reihenfolge einer Aufzählung zählt nicht.
 */
function valueIdentity(field: IDSValueField | undefined): string {
  if (!field) return '';
  const fold = (value: string | undefined): string => (value ?? '').trim().toLowerCase();
  switch (field.dataType) {
    case 'Wert':
      return fold(field.simpleValue);
    case 'Aufzählung':
      return (field.enumValues ?? []).map(fold).filter(value => value !== '').sort().join(',');
    default:
      return normalize(field, true);
  }
}

/**
 * Ordnet die Facetten eines Blocks einander zu (siehe Kopfkommentar) und vergleicht die Paare
 * feldgenau. Pfade zählen in `proposed`, entfernte Facetten in `current`.
 */
function diffFacetBlock(
  block: IdsFacetBlock,
  current: IDSFacet[],
  proposed: IDSFacet[],
  into: Map<string, MyCheckFieldChange>,
  removedInto: RemovedIdsFacet[],
): void {
  const pairs = matchFacets(current, proposed);

  proposed.forEach((proposedFacet, i) => {
    const currentIndex = pairs.get(i);
    if (currentIndex === undefined) {
      into.set(facetPath(block, i), {before: undefined, after: proposedFacet});
      return;
    }
    const currentFacet = current[currentIndex];
    const caseInsensitive = CASE_INSENSITIVE_FIELDS[currentFacet.type] ?? [];
    const fields = [
      ...FACET_FIELDS[currentFacet.type],
      ...(block === 'requirements' ? REQUIREMENT_META_FIELDS : []),
    ];
    for (const field of fields) {
      diffField(facetFieldPath(block, i, field), currentFacet[field], proposedFacet[field], into, caseInsensitive.includes(field));
    }
  });

  const pairedCurrent = new Set(pairs.values());
  current.forEach((facet, index) => {
    if (!pairedCurrent.has(index)) removedInto.push({block, facet, index});
  });
}

/** Index im Vorschlag → Index im Ist-Stand, für alle gepaarten Facetten. */
function matchFacets(current: IDSFacet[], proposed: IDSFacet[]): Map<number, number> {
  const pairs = new Map<number, number>();
  const byIdentity = new Map<string, number[]>();
  current.forEach((facet, index) => {
    const key = facetIdentity(facet);
    byIdentity.set(key, [...(byIdentity.get(key) ?? []), index]);
  });
  proposed.forEach((facet, i) => {
    const currentIndex = byIdentity.get(facetIdentity(facet))?.shift();
    if (currentIndex !== undefined) pairs.set(i, currentIndex);
  });

  // Übrige der Reihe nach paaren, aber nur bei gleichem Typ – etwa eine umbenannte Eigenschaft.
  const pairedCurrent = new Set(pairs.values());
  const leftoverCurrent = current.map((_, index) => index).filter(index => !pairedCurrent.has(index));
  const leftoverProposed = proposed.map((_, i) => i).filter(i => !pairs.has(i));
  leftoverProposed.forEach((i, position) => {
    const currentIndex = leftoverCurrent[position];
    if (currentIndex !== undefined && current[currentIndex].type === proposed[i].type) {
      pairs.set(i, currentIndex);
    }
  });
  return pairs;
}

function diffField(
  path: string,
  before: unknown,
  after: unknown,
  into: Map<string, MyCheckFieldChange>,
  ignoreCase = false,
): void {
  if (normalize(before, ignoreCase) !== normalize(after, ignoreCase)) {
    into.set(path, {before, after});
  }
}

/**
 * Vergleichsform eines Werts: leere Strings, `null`/`undefined` und leere Arrays gelten als „nicht
 * gesetzt", Objekt-Schlüssel werden sortiert, das reine UI-Flag `expanded` und die pro Parse neu
 * vergebenen `id`s fallen weg. Mit `ignoreCase` zählt die Groß-/Kleinschreibung der Strings nicht.
 */
function normalize(value: unknown, ignoreCase = false): string {
  return JSON.stringify(canonical(value, ignoreCase)) ?? '';
}

function canonical(value: unknown, ignoreCase: boolean): unknown {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return undefined;
    return ignoreCase ? trimmed.toLowerCase() : trimmed;
  }
  if (Array.isArray(value)) {
    const items = value.map(item => canonical(item, ignoreCase)).filter(item => item !== undefined);
    return items.length ? items : undefined;
  }
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      if (key === 'id' || key === 'expanded') continue;
      const item = canonical((value as Record<string, unknown>)[key], ignoreCase);
      if (item !== undefined) result[key] = item;
    }
    return Object.keys(result).length ? result : undefined;
  }
  return value;
}
